import { queryClient } from '@/router'
import { useMutation } from '@tanstack/react-query'
import {
  CalendarClockIcon,
  CheckSquareIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  FileTextIcon,
  HashIcon,
  SmileIcon,
  Undo2Icon,
  UserIcon,
} from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/button/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/table/table'

import { API_ENDPOINTS } from '@/lib/constants/api'
import { updateTaskStatusMutation } from '@/lib/queries/business/v1/task/complete/mutations'
import { type DashboardTaskItem } from '@/lib/queries/business/v1/task/search/types'
import { cn } from '@/lib/utils'

import { CellContentAssignees } from './table-cell-content/cell-content-assignees'
import { CellContentCaseLink } from './table-cell-content/cell-content-case-link'
import { CellContentCompleted } from './table-cell-content/cell-content-completed'

const DATA_DISPLAY_COUNT_ON_MOUNT = 5

export interface ActionCompletedTableProps {
  data: DashboardTaskItem[]
}

export const ActionCompletedTable = ({ data }: ActionCompletedTableProps) => {
  const { mutateAsync: changeTaskStatus } = useMutation(updateTaskStatusMutation)

  const [nowUpdatingTaskIds, setNowUpdatingTaskIds] = useState<number[]>([])
  const [showsEntireData, setShowsEntireData] = useState(false)

  const renderedData = !showsEntireData ? data.slice(0, DATA_DISPLAY_COUNT_ON_MOUNT) : data

  const showsViewMoreButton = data.length > DATA_DISPLAY_COUNT_ON_MOUNT

  const handleClickRevertActionButton = async (taskId: number) => {
    setNowUpdatingTaskIds((prev) => [...prev, taskId])
    await changeTaskStatus(
      [
        {
          id: taskId,
          isComplete: false,
        },
      ],
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey: [API_ENDPOINTS.business.v1.task.search.index],
          })
          toast.success('Task has been reverted to Action Required!')
        },
        onError: () => {
          toast.error('Failed to revert task. Please try again later.')
        },
        onSettled: () => {
          setNowUpdatingTaskIds((prev) => prev.filter((id) => id !== taskId))
        },
      }
    )
  }

  const handleClickViewMoreButton = () => {
    setShowsEntireData((prev) => !prev)
  }

  if (data.length === 0) {
    return <p className={cn('text-center text-gray-500', 'py-3')}>No tasks yet.</p>
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow className={cn('hover:bg-transparent')}>
            <TableHead>
              <p className={cn('flex items-center gap-x-1')}>
                <SmileIcon size={16} className={cn('min-w-4')} />
                Applicant
              </p>
            </TableHead>
            <TableHead>
              <p className={cn('flex items-center gap-x-1')}>
                <FileTextIcon size={16} className={cn('min-w-4')} />
                Case Type
              </p>
            </TableHead>
            <TableHead>
              <p className={cn('flex items-center gap-x-1')}>
                <HashIcon size={16} className={cn('min-w-4')} />
                Case Number
              </p>
            </TableHead>
            <TableHead>
              <p className={cn('flex items-center gap-x-1')}>
                <UserIcon size={16} className={cn('min-w-4')} />
                Assignee
              </p>
            </TableHead>
            <TableHead>
              <p className={cn('flex items-center gap-x-1')}>
                <CalendarClockIcon size={16} className={cn('min-w-4')} />
                Pending Day
              </p>
            </TableHead>
            <TableHead>
              <p className={cn('flex items-center gap-x-1')}>
                <CheckSquareIcon size={16} className={cn('min-w-4')} />
                Completed
              </p>
            </TableHead>
            {/* Intentional empty cell */}
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {renderedData.map(
            ({
              taskId,
              applicantName,
              applicantPathId,
              caseType,
              caseNumber,
              assigneeNames,
              pendingDays,
              completeUserName,
              completedDate,
            }) => (
              <TableRow key={taskId} className={cn('hover:bg-transparent', 'text-slate-900')}>
                <TableCell>{applicantName}</TableCell>
                <TableCell>{caseType}</TableCell>
                <TableCell>
                  <CellContentCaseLink applicantId={`${applicantPathId}`} caseNumber={caseNumber} />
                </TableCell>
                <TableCell>
                  <CellContentAssignees assignees={assigneeNames} />
                </TableCell>
                <TableCell>
                  {typeof pendingDays !== 'number' && '-'}
                  {typeof pendingDays === 'number' && `${pendingDays} ${pendingDays >= 2 ? 'days' : 'day'}`}
                </TableCell>
                <TableCell>
                  <CellContentCompleted taskId={taskId} completedBy={completeUserName} completedAt={completedDate} />
                </TableCell>
                <TableCell className={cn('text-right')}>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className={cn('inline-flex items-center gap-x-1')}
                    loading={nowUpdatingTaskIds.includes(taskId)}
                    onClick={() => handleClickRevertActionButton(taskId)}
                  >
                    <Undo2Icon size={16} />
                    <span>Back To Action</span>
                  </Button>
                </TableCell>
              </TableRow>
            )
          )}
        </TableBody>
      </Table>

      {showsViewMoreButton && <ViewMore showsEntireData={showsEntireData} onClick={handleClickViewMoreButton} />}
    </>
  )
}

const ViewMore = ({ showsEntireData, onClick }: { showsEntireData: boolean; onClick: VoidFunction }) => {
  return (
    <div className={cn('flex justify-center', 'w-full', 'mt-3', 'text-slate-500')}>
      <button type="button" className={cn('inline-flex items-center gap-x-2.5')} onClick={onClick}>
        <span>{showsEntireData ? 'View Less' : 'View More'}</span>
        {showsEntireData ? <ChevronUpIcon size={16} /> : <ChevronDownIcon size={16} />}
      </button>
    </div>
  )
}
